import React, { useEffect, useState } from "react";
import api, { formatApiError, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileText, Trash, DownloadSimple, UploadSimple, WhatsappLogo, EnvelopeSimple, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import DownloadDialog from "@/components/DownloadDialog";

const DOC_CATEGORIES = {
  "Identity Proof": ["Aadhaar Card", "PAN Card", "Voter ID", "Passport", "Driving Licence"],
  "Address Proof": ["Aadhaar", "Electricity Bill", "Water Bill", "Telephone Bill", "Bank Passbook", "Rent Agreement"],
  "Educational": ["SSC Marksheet", "HSC Marksheet", "Degree Certificate", "Leaving Certificate"],
  "Income": ["Income Certificate", "Salary Slip", "Form 16", "ITR", "Bank Statement"],
  "Property": ["7/12 Extract", "8A Extract", "Property Card", "Sale Deed", "Tax Receipt"],
  "Other": ["Birth Certificate", "Death Certificate", "Marriage Certificate", "Passport Size Photo", "Signature", "Affidavit", "Self Declaration", "Caste Validity Certificate", "Disability Certificate"],
};

const CAT_TONE = {
  "Identity Proof": "bg-orange-50 text-orange-700 border-orange-200",
  "Address Proof": "bg-blue-50 text-blue-700 border-blue-200",
  "Educational": "bg-purple-50 text-purple-700 border-purple-200",
  "Income": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Property": "bg-amber-50 text-amber-700 border-amber-200",
  "Other": "bg-slate-100 text-slate-700 border-slate-200",
};

export default function DocumentsTab({ customerId }) {
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [downloadDoc, setDownloadDoc] = useState(null);
  const [customerMobile, setCustomerMobile] = useState("");
  const [form, setForm] = useState({ category: "Identity Proof", doc_type: "Aadhaar Card", expiry: "", notes: "", file_name: "", mime: "", data_base64: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/customers/${customerId}/documents`);
    setDocs(data);
  };
  useEffect(() => {
    if (customerId) {
      load();
      api.get(`/customers/${customerId}`).then((r) => setCustomerMobile(r.data?.mobile || "")).catch(() => {});
    }
  }, [customerId]);

  const onFile = (file) => {
    if (!file) return;
    if (file.size > 4_000_000) { toast.error("File too large (max 4 MB)"); return; }
    const r = new FileReader();
    r.onload = () => setForm((f) => ({ ...f, file_name: file.name, mime: file.type, data_base64: r.result }));
    r.readAsDataURL(file);
  };

  const submit = async () => {
    if (!form.file_name || !form.data_base64) { toast.error("Choose a file"); return; }
    setSaving(true);
    try {
      await api.post(`/customers/${customerId}/documents`, form);
      toast.success("Document uploaded");
      setOpen(false);
      setForm({ category: "Identity Proof", doc_type: "Aadhaar Card", expiry: "", notes: "", file_name: "", mime: "", data_base64: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try { await api.delete(`/documents/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const download = (d) => {
    // Quick original download shortcut (still available)
    const a = document.createElement("a");
    a.href = d.data_base64;
    a.download = d.file_name;
    a.click();
  };

  const shareWhatsApp = (d) => {
    const num = (customerMobile || "").replace(/\D/g, "");
    const text = encodeURIComponent(`DigitalSetu – ${d.doc_type} (${d.file_name}). Kindly find your document attached (please request via SMS or email if not received).`);
    const url = num ? `https://wa.me/91${num}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(url, "_blank");
  };

  const shareEmail = (d) => {
    const subject = encodeURIComponent(`${d.doc_type} - ${d.file_name}`);
    const body = encodeURIComponent(`Hello,\n\nPlease find your ${d.doc_type} shared by DigitalSetu.\n\n(Original file: ${d.file_name})\n\nBest regards,\nDigitalSetu`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const grouped = docs.reduce((acc, d) => {
    (acc[d.category] = acc[d.category] || []).push(d);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">{docs.length} document{docs.length === 1 ? "" : "s"} on file</div>
        <Button data-testid="doc-upload-btn" onClick={() => setOpen(true)} className="bg-orange-600 hover:bg-orange-700 text-white gap-2 h-9">
          <UploadSimple size={16} weight="bold" /> Upload
        </Button>
      </div>

      {docs.length === 0 && (
        <Card className="p-10 text-center bg-white border-dashed border-slate-300">
          <FileText size={40} className="mx-auto text-slate-300 mb-3" weight="duotone" />
          <div className="text-sm text-slate-600">No documents yet. Upload identity, address, income and other proofs here.</div>
        </Card>
      )}

      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat}>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">{cat} ({items.length})</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {items.map((d) => (
              <Card key={d.id} className="p-3 bg-white border-slate-200 hover:border-orange-300 transition-colors" data-testid={`doc-${d.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                    <FileText size={20} className="text-slate-600" weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-900 text-sm">{d.doc_type}</span>
                      <Badge variant="outline" className={`text-[9px] uppercase tracking-widest ${CAT_TONE[d.category] || CAT_TONE.Other}`}>{d.category}</Badge>
                    </div>
                    <div className="text-xs text-slate-500 truncate mt-0.5">{d.file_name}</div>
                    {d.expiry && <div className="text-xs text-amber-700 mt-0.5">Expires {d.expiry}</div>}
                  </div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => setPreview(d)} data-testid={`doc-preview-${d.id}`} title="Preview">
                      <Eye size={16} />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => setDownloadDoc(d)} data-testid={`doc-download-${d.id}`} title="Download (KB / format / preset)">
                      <DownloadSimple size={16} className="text-orange-600" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => shareWhatsApp(d)} data-testid={`doc-wa-${d.id}`} title="Share via WhatsApp" className="text-emerald-600 hover:bg-emerald-50">
                      <WhatsappLogo size={16} />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => shareEmail(d)} data-testid={`doc-mail-${d.id}`} title="Share via Email" className="text-blue-600 hover:bg-blue-50">
                      <EnvelopeSimple size={16} />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => del(d.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`doc-delete-${d.id}`} title="Delete">
                      <Trash size={16} />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      ))}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle style={{ fontFamily: "var(--font-heading)" }}>Upload Document</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <F label="Category">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v, doc_type: DOC_CATEGORIES[v][0] })}>
                <SelectTrigger data-testid="doc-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.keys(DOC_CATEGORIES).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Document Type">
              <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{(DOC_CATEGORIES[form.category] || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="File (max 4 MB · PDF / Image)">
              <input type="file" accept="image/*,.pdf" data-testid="doc-file-input"
                onChange={(e) => onFile(e.target.files?.[0])}
                className="text-sm file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-slate-900 file:text-white file:cursor-pointer" />
              {form.file_name && <div className="text-xs text-slate-500 mt-2">Selected: {form.file_name}</div>}
            </F>
            <F label="Expiry Date (optional)">
              <Input type="date" value={form.expiry} onChange={(e) => setForm({ ...form, expiry: e.target.value })} />
            </F>
            <F label="Notes (optional)">
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </F>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="doc-save-btn" onClick={submit} disabled={saving} className="bg-orange-600 hover:bg-orange-700 text-white">{saving ? "Uploading…" : "Upload"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="max-w-3xl bg-white">
          <DialogHeader><DialogTitle style={{ fontFamily: "var(--font-heading)" }}>{preview?.doc_type}</DialogTitle></DialogHeader>
          {preview && (
            <div className="py-2">
              {preview.mime?.startsWith("image/") ? (
                <img src={preview.data_base64} alt="" className="max-h-[70vh] mx-auto rounded border" />
              ) : preview.mime === "application/pdf" ? (
                <iframe src={preview.data_base64} title="pdf" className="w-full h-[70vh] rounded border" />
              ) : (
                <div className="text-sm text-slate-600 py-10 text-center">Preview not supported. <button className="text-orange-600 underline" onClick={() => download(preview)}>Download</button> instead.</div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <DownloadDialog doc={downloadDoc} open={!!downloadDoc} onOpenChange={(v) => !v && setDownloadDoc(null)} />
    </div>
  );
}

function F({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-600">{label}</Label>
      {children}
    </div>
  );
}
